import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import {
  LocalShipping,
  ChevronLeft,
  CheckCircle,
  Cancel,
  Person,
  CreditCard,
  Phone,
  Email,
  LocationOn,
  Work
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { quikqAuthService } from '../services/quikqAuth';
import { quikqApiService, DriverInfo, CardInfo } from '../services/quikqApi';

const Drivers: React.FC = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<DriverInfo[]>([]);
  const [driverCards, setDriverCards] = useState<{ [driverId: string]: CardInfo[] }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch drivers data
  const fetchDrivers = async () => {
    setLoading(true);
    setError('');
    try {
      const currentUser = quikqAuthService.getCurrentUser();
      if (!currentUser?.startCode) {
        throw new Error('No start code found');
      }

      const driversData = await quikqApiService.getCarrierDrivers(currentUser.startCode);
      // console.log('🔍 Drivers data:', driversData);
      setDrivers(driversData);

      // Fetch cards for each driver
      const cardsMap: { [driverId: string]: CardInfo[] } = {};
      for (const driver of driversData) {
        try {
          const cards = await quikqApiService.getCarrierCards(currentUser.startCode);
          // Filter cards for this specific driver
          const driverCards = cards.filter(card => card.assetId === driver.driverId);
          cardsMap[driver.driverId] = driverCards;
        } catch (err) {
          // console.log(`⚠️ Could not fetch cards for driver ${driver.driverId}:`, err);
          cardsMap[driver.driverId] = [];
        }
      }
      setDriverCards(cardsMap);



    } catch (error) {
      console.error('Error fetching drivers:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  const handleBack = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ pb: { xs: 12, sm: 12, md: 12 } }}>
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ pb: { xs: 12, sm: 12, md: 12 } }}>
      <Box sx={{ mt: 4 }}>
              {/* Header with Split Watermark - Left: NSFLogo, Right: Empty (for future custom logo) */}
      <Box sx={{ 
        position: 'relative', 
        mb: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          left: -50,
          width: '100%',
          height: '200px',
          background: `url('/NSFIcon.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: 'none'
        }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            position: 'relative',
            zIndex: 1
          }}>
            <Box
              onClick={handleBack}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: 'text.primary',
                mr: 2,
                '&:hover': { opacity: 0.7 }
              }}
            >
              <ChevronLeft sx={{ fontSize: 20, mr: 0.5 }} />
              <Typography variant="body2" fontWeight="500">Back</Typography>
            </Box>
          </Box>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ position: 'relative', zIndex: 1 }}>
            Drivers Information
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

                 {/* Drivers Overview */}
         <Card sx={{ mb: 3 }}>
           <CardContent>
             <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
               <LocalShipping sx={{ mr: 1, color: 'primary.main' }} />
               <Typography variant="h6">
                 Drivers Overview
               </Typography>
             </Box>
             <Box sx={{ 
               display: 'grid', 
               gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)' }, 
               gap: { xs: 1, sm: 2 },
               '& > *': {
                 p: { xs: 1, sm: 2 },
                 fontSize: { xs: '0.875rem', sm: '1rem' }
               }
             }}>
               <Box sx={{ 
                 textAlign: 'center', 
                 p: { xs: 1, sm: 2 }, 
                 bgcolor: 'primary.light', 
                 borderRadius: 1,
                 minHeight: { xs: 80, sm: 'auto' },
                 display: 'flex',
                 flexDirection: 'column',
                 justifyContent: 'center',
                 alignItems: 'center'
               }}>
                 <Typography variant="h4" color="white" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, mb: 0.5 }}>
                   {drivers.length}
                 </Typography>
                 <Typography variant="body2" color="white" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                   Total Drivers
                 </Typography>
               </Box>
               <Box sx={{ 
                 textAlign: 'center', 
                 p: { xs: 1, sm: 2 }, 
                 bgcolor: 'success.light', 
                 borderRadius: 1,
                 minHeight: { xs: 80, sm: 'auto' },
                 display: 'flex',
                 flexDirection: 'column',
                 justifyContent: 'center',
                 alignItems: 'center'
               }}>
                 <Typography variant="h4" color="white" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, mb: 0.5 }}>
                   {drivers.filter(d => d.isActive).length}
                 </Typography>
                 <Typography variant="body2" color="white" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                   Active Drivers
                 </Typography>
               </Box>
               <Box sx={{ 
                 textAlign: 'center', 
                 p: { xs: 1, sm: 2 }, 
                 bgcolor: 'warning.light', 
                 borderRadius: 1,
                 minHeight: { xs: 80, sm: 'auto' },
                 display: 'flex',
                 flexDirection: 'column',
                 justifyContent: 'center',
                 alignItems: 'center'
               }}>
                 <Typography variant="h4" color="white" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, mb: 0.5 }}>
                   {drivers.filter(d => !d.isActive).length}
                 </Typography>
                 <Typography variant="body2" color="white" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                   Inactive Drivers
                 </Typography>
               </Box>
             </Box>
           </CardContent>
         </Card>

        {/* Drivers List */}
        {drivers.length === 0 ? (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LocalShipping sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No Drivers Found
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  There are currently no drivers assigned to this carrier
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          drivers.map((driver, index) => (
            <Card key={driver.driverId} sx={{ mb: 3 }}>
              <CardContent>
                {/* Driver Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: driver.isActive ? 'success.main' : 'error.main' }}>
                    <Person sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {driver.firstName} {driver.lastName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip 
                        icon={driver.isActive ? <CheckCircle /> : <Cancel />}
                        label={driver.isActive ? 'ACTIVE' : 'INACTIVE'}
                        size="small"
                        color={driver.isActive ? 'success' : 'error'}
                      />
                      {driver.driverType && (
                        <Chip 
                          label={driver.driverType}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </Box>

                                 {/* Driver Details */}
                 <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
                   <Box sx={{ flex: 1 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                       <Work sx={{ mr: 1, color: 'text.secondary' }} />
                       <Typography variant="body2" color="textSecondary">
                         Driver ID:
                       </Typography>
                       <Typography variant="body1" sx={{ ml: 1, fontWeight: 500 }}>
                         {driver.driverId}
                       </Typography>
                     </Box>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                       <CreditCard sx={{ mr: 1, color: 'text.secondary' }} />
                       <Typography variant="body2" color="textSecondary">
                         License Number:
                       </Typography>
                       <Typography variant="body1" sx={{ ml: 1, fontWeight: 500 }}>
                         {driver.licenseNumber}
                         {driver.licenseState && ` (${driver.licenseState})`}
                       </Typography>
                     </Box>
                     <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                       <LocalShipping sx={{ mr: 1, color: 'text.secondary' }} />
                       <Typography variant="body2" color="textSecondary">
                         Unit Assigned:
                       </Typography>
                       <Typography variant="body1" sx={{ ml: 1, fontWeight: 500 }}>
                         {driver.unitAssigned || 'N/A'}
                       </Typography>
                     </Box>
                     {driver.emailAddress && (
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                         <Email sx={{ mr: 1, color: 'text.secondary' }} />
                         <Typography variant="body2" color="textSecondary">
                           Email:
                         </Typography>
                         <Typography variant="body1" sx={{ ml: 1 }}>
                           {driver.emailAddress}
                         </Typography>
                       </Box>
                     )}
                     {driverCards[driver.driverId] && driverCards[driver.driverId].length > 0 && (
                       driverCards[driver.driverId].map((card, cardIndex) => (
                         <Box key={cardIndex} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                           <CreditCard sx={{ mr: 1, color: 'text.secondary' }} />
                           <Typography variant="body2" color="textSecondary">
                             Card Associated:
                           </Typography>
                           <Typography variant="body1" sx={{ ml: 1, fontWeight: 500 }}>
                             {card.cardNo}
                           </Typography>
                         </Box>
                       ))
                     )}
                   </Box>
                   <Box sx={{ flex: 1 }}>
                     {driver.phone && driver.phone.length > 0 && (
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                         <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                         <Typography variant="body2" color="textSecondary">
                           Phone:
                         </Typography>
                         <Typography variant="body1" sx={{ ml: 1 }}>
                           {driver.phone[0].phone} ({driver.phone[0].type})
                         </Typography>
                       </Box>
                     )}
                     {driver.address && driver.address.length > 0 && (
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                         <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                         <Typography variant="body2" color="textSecondary">
                           Address:
                         </Typography>
                         <Typography variant="body1" sx={{ ml: 1 }}>
                           {driver.address[0].line1}, {driver.address[0].city}, {driver.address[0].state} {driver.address[0].zip}
                         </Typography>
                       </Box>
                     )}
                   </Box>
                 </Box>

                {/* Additional Driver Info */}
                {driver.division && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Division: {driver.division}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))
        )}

        {/* Refresh Button */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={fetchDrivers}
            startIcon={<LocalShipping />}
          >
            Refresh Drivers
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default Drivers;
